import React, { useEffect, useState, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  typeIcons,
  IconLink,
  InlineEdit,
  useFieldOptions,
  fieldOptionList,
  formatAddress,
  locationLabel,
} from "../components/Utils";
import axios from "../axiosConfig";
import NotesTable from "../components/NotesTable";
import TasksTable from "../components/TasksTable";
import InteractionsTable from "../components/InteractionsTable";
import Modal from "../components/Modal";
import { useTaskRefresh } from "../context/TaskRefreshContext.jsx";
import QuickEmail from "../components/QuickEmail";
import Table from "../components/Table";
import { FaCircleNotch } from "react-icons/fa";
import "./Job.css";
import Checkbox from '../components/Checkbox';
import { parseDbDate } from '../utils/dates';
import PageTitle from "../components/PageTitle";

// A job's start/end date ('YYYY-MM-DD') as dd/mm/yyyy, or "".
const formatJobDate = (value) => parseDbDate(value)?.toLocaleDateString("en-GB") ?? "";

// A contact's name (a link to them) with their email underneath.
const ContactWithEmail = ({ contact }) => (
  <span>
    <Link to={`/contact?id=${contact.id}`}>
      {contact.first_name} {contact.last_name}
    </Link>
    {contact.email && <div className="details-subline">{contact.email}</div>}
  </span>
);

// "Teams - FinanceTalking" etc., or "" when the job has no virtual setup.
const virtualSetupLabel = (job) =>
  job?.virtual_type && job?.virtual_platform
    ? `${job.virtual_platform} - ${job.virtual_type}`
    : "";

// Location label with its full address in brackets, for the job's location fields.
const locationWithAddress = (loc) => {
  if (!loc) return "";
  const addr = formatAddress(loc);
  return addr ? `${locationLabel(loc)} (${addr})` : locationLabel(loc);
};

const Job = () => {
  const [job, setJob] = useState(null);
  const fieldOptions = useFieldOptions();
  const refreshTasks = useTaskRefresh();
  const [loading, setLoading] = useState(true);
  const linkedComponentTooltip =
    "This component is linked. When used in a programme, any changes to the saved component will be updated in the programme. If this component is changed in a programme it will become unlinked.";
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [user, setUser] = useState(null);
  const [creatingProposal, setCreatingProposal] = useState(false);
  const [creatingBookingForm, setCreatingBookingForm] = useState(false);
  const [proposalPreviewUrl, setProposalPreviewUrl] = useState(null);
  const [proposalPreviewLoading, setProposalPreviewLoading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );
  const [creatingSlideshow, setCreatingSlideshow] = useState(false);
  const [showObjectivesModal, setShowObjectivesModal] = useState(false);
  const [objectives, setObjectives] = useState({
    objective1: "",
    objective2: "",
    objective3: "",
    objective4: "",
    objective5: "",
  });
  const [objectiveOptions, setObjectiveOptions] = useState([]);
  const [overviewOptions, setOverviewOptions] = useState([]);
  const [selectedOverview, setSelectedOverview] = useState([]);
  const [programmes, setProgrammes] = useState(null);
  const [programmesLoading, setProgrammesLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [sessionFormData, setSessionFormData] = useState({
    title: "",
    session_date: "",
    start_time: "",
    end_time: "",
    duration_quantity: "",
    duration_time: "",
    type: "",
    location_id: "",
    virtual_link: "",
  });
  const [allTrainingLocations, setAllTrainingLocations] = useState([]);
  const [showCreateSessionsModal, setShowCreateSessionsModal] = useState(false);
  const [createSessionsLocationId, setCreateSessionsLocationId] = useState("");
  const [creatingSession, setCreatingSession] = useState(false);
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [sessionDeleteConfirm, setSessionDeleteConfirm] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [deletingSession, setDeletingSession] = useState(false);
  const [invoiceLocations, setInvoiceLocations] = useState([]);

  // References to table components for cross-refreshing
  const tasksTableRef = useRef(null);
  const interactionsTableRef = useRef(null);

  const location = useLocation();
  const id = new URLSearchParams(location.search).get("id");

  // Fetch job data
  useEffect(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      if (storedUser) {
        setUser(storedUser);
      }
    } catch {
      console.warn("Invalid user in localStorage");
    }

    const fetchJob = async () => {
      try {
        const response = await axios.get(`/jobs/${id}/`, {
          withCredentials: true,
        });
        const data = response.data;
        console.log("Job data:", data);

        setJob(data);

        // Set sessions data
        setSessions(data.sessions || []);

        // Load proposal objectives if present
        if (data.proposal) {
          setObjectives({
            objective1: data.proposal.objective1 || "",
            objective2: data.proposal.objective2 || "",
            objective3: data.proposal.objective3 || "",
            objective4: data.proposal.objective4 || "",
            objective5: data.proposal.objective5 || "",
          });
          if (data.proposal.overview) {
            setSelectedOverview(data.proposal.overview);
          }
        }

        // Update page title
        const isStaging =
          (process.env.REACT_APP_ENVIRONMENT || "").toLowerCase() === "staging";
        document.title = `${data.title || "Job"} - FT Portal${isStaging ? " (Staging)" : ""}`;
      } catch (error) {
        console.error("Error fetching job:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [id]);

  // Reset page title on unmount
  useEffect(() => {
    const isStaging =
      (process.env.REACT_APP_ENVIRONMENT || "").toLowerCase() === "staging";
    return () => {
      document.title = isStaging ? "FT Portal (Staging)" : "FT Portal";
    };
  }, []);

  // Fetch all locations once for the training location dropdown.
  // The dropdown shows: client account's locations + all other accounts' venue=true locations.
  useEffect(() => {
    axios
      .get("/locations/", { withCredentials: true })
      .then((res) =>
        setAllTrainingLocations(Array.isArray(res.data) ? res.data : []),
      )
      .catch((err) => console.error("Failed to load locations:", err));
  }, []);

  // Invoice location options are the Billing Address locations of the invoice account
  useEffect(() => {
    const invoiceAccountId = job?.invoice_account_data?.id;
    if (!invoiceAccountId) {
      setInvoiceLocations([]);
      return;
    }
    let cancelled = false;
    axios
      .get(`/accounts/${invoiceAccountId}/`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        setInvoiceLocations(
          (res.data.locations || []).filter((l) => l.type === "Billing Address"),
        );
      })
      .catch((err) =>
        console.error("Failed to load invoice account locations:", err),
      );
    return () => {
      cancelled = true;
    };
  }, [job?.invoice_account_data?.id]);

  // Inline edit save handler for the details table.
  // Refetches the full job afterwards (rather than merging local state) because
  // changing programme_id has a server-side side effect (clears sessions_components),
  // and several fields drive nested *_data lookups that are easier to get from the
  // server than to reconstruct on the client.
  const handleInlineSave = async (field, newValue) => {
    try {
      let payload;

      if (field === "account") {
        payload = { account_id: newValue?.id || null };
      } else if (field === "invoice_account") {
        payload = { invoice_account_id: newValue?.id || null };
      } else if (field === "invoicing_platform_account") {
        payload = { invoicing_platform_account_id: newValue?.id || null };
      } else if (field === "ftl_contact") {
        payload = { ftl_contact_id: newValue?.id || null };
      } else if (field === "account_contact_1") {
        // Clearing contact 1 moves contact 2 (if any) up into its place.
        payload = newValue?.id
          ? { account_contact_id_1: newValue.id }
          : {
              account_contact_id_1: job.account_contact_id_2 || null,
              account_contact_id_2: null,
            };
      } else if (field === "account_contact_2") {
        payload = { account_contact_id_2: newValue?.id || null };
      } else if (field === "invoice_contact") {
        payload = { invoice_contact_id: newValue?.id || null };
      } else if (field === "virtual_setup") {
        if (newValue) {
          const [type, platform] = newValue.split("|");
          payload = { virtual_type: type, virtual_platform: platform };
        } else {
          payload = { virtual_type: null, virtual_platform: null };
        }
      } else if (["stage", "category", "sales_channel"].includes(field)) {
        payload = { [field]: newValue || null };
      } else if (field === "default_training_location") {
        payload = {
          default_training_location_id: newValue
            ? parseInt(newValue, 10)
            : null,
        };
      } else if (field === "invoice_location") {
        payload = {
          invoice_location_id: newValue ? parseInt(newValue, 10) : null,
        };
      } else if (field === "programme") {
        payload = { programme_id: newValue ? parseInt(newValue, 10) : null };
      } else {
        payload = { [field]: newValue };
      }

      const res = await axios.patch(`/jobs/${job.id}/`, payload, {
        withCredentials: true,
      });
      if (res.data.success) {
        const jobResponse = await axios.get(`/jobs/${id}/`, {
          withCredentials: true,
        });
        const updated = jobResponse.data;
        setJob(updated);
        setSessions(updated.sessions || []);
      }
    } catch (err) {
      console.error("Failed to update job field:", err);
    }
  };

  // Session form change handler
  const handleSessionFormChange = (e) => {
    const { name, value } = e.target;
    setSessionFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleComponentToggle = (componentId) => {
    setSelectedComponents((prev) =>
      prev.includes(componentId) ? [] : [componentId]
    );
  };

  // Reset the session form and close the create/edit modal
  const closeSessionModal = () => {
    setShowCreateSession(false);
    setEditingSessionId(null);
    setSessionFormData({
      title: "",
      session_date: "",
      start_time: "",
      end_time: "",
      duration_quantity: "",
      duration_time: "",
      type: "",
      location_id: "",
      virtual_link: "",
    });
    setSelectedComponents([]);
  };

  // Open the modal in "create" mode, defaulting location to the job's default training location
  const openAddSessionModal = () => {
    setEditingSessionId(null);
    setSessionFormData({
      title: "",
      session_date: "",
      start_time: "",
      end_time: "",
      duration_quantity: "",
      duration_time: "",
      type: "",
      location_id: job?.default_training_location_id
        ? String(job.default_training_location_id)
        : "",
      virtual_link: job?.default_virtual_link || "",
    });
    setSelectedComponents([]);
    setShowCreateSession(true);
  };

  // Open the modal in "edit" mode, prefilled with the session's current data
  const openEditSessionModal = async (row) => {
    setEditingSessionId(row.id);
    setSessionFormData({
      title: row.title || "",
      session_date: row.start_time ? row.start_time.slice(0, 10) : "",
      start_time: row.start_time ? row.start_time.slice(11, 16) : "",
      end_time: row.end_time ? row.end_time.slice(11, 16) : "",
      duration_quantity: row.duration_quantity || "",
      duration_time: row.duration_time || "",
      type: row.type || "",
      location_id: row.location_id ? String(row.location_id) : "",
      virtual_link: row.virtual_link || "",
    });
    setShowCreateSession(true);
    try {
      const response = await axios.get(`/sessions/${row.id}/`, {
        withCredentials: true,
      });
      setSelectedComponents(response.data.assigned_component_ids || []);
    } catch (err) {
      console.error("Failed to load session details:", err);
    }
  };

  // Create or update a session, depending on whether we're editing an existing one
  const handleSaveSession = async () => {
    setCreatingSession(true);
    try {
      const start_dt = sessionFormData.session_date
        ? `${sessionFormData.session_date} ${sessionFormData.start_time || '00:00'}:00`
        : null;
      const end_dt = sessionFormData.session_date
        ? `${sessionFormData.session_date} ${sessionFormData.end_time || '00:00'}:00`
        : null;
      const payload = {
        title: sessionFormData.title || null,
        start_time: start_dt,
        end_time: end_dt,
        duration_quantity: sessionFormData.duration_quantity || null,
        duration_time: sessionFormData.duration_time || null,
        type: sessionFormData.type || null,
        location_id: sessionFormData.location_id
          ? parseInt(sessionFormData.location_id, 10)
          : null,
        virtual_link: sessionFormData.virtual_link || null,
        component_ids: selectedComponents,
      };

      const response = editingSessionId
        ? await axios.patch(`/sessions/${editingSessionId}/`, payload, {
            withCredentials: true,
          })
        : await axios.post(
            "/sessions/",
            { ...payload, job_id: job.id },
            { withCredentials: true },
          );

      if (response.data.success) {
        // Refetch the job to get updated sessions
        const jobResponse = await axios.get(`/jobs/${id}/`, {
          withCredentials: true,
        });
        const updated = jobResponse.data;
        setJob(updated);
        setSessions(updated.sessions || []);
        closeSessionModal();
      }
    } catch (err) {
      console.error("Failed to save session:", err);
      alert("Failed to save session");
    } finally {
      setCreatingSession(false);
    }
  };

  // Open the delete confirmation modal for a session
  const openDeleteSessionConfirm = (row) => {
    setSessionToDelete(row);
    setSessionDeleteConfirm(true);
  };

  // Delete a session
  const handleDeleteSession = async () => {
    if (!sessionToDelete) return;
    setDeletingSession(true);
    try {
      await axios.delete(`/sessions/${sessionToDelete.id}/`, {
        withCredentials: true,
      });
      const jobResponse = await axios.get(`/jobs/${id}/`, {
        withCredentials: true,
      });
      const updated = jobResponse.data;
      setJob(updated);
      setSessions(updated.sessions || []);
      setSessionDeleteConfirm(false);
      setSessionToDelete(null);
    } catch (err) {
      console.error("Failed to delete session:", err);
      alert("Failed to delete session");
    } finally {
      setDeletingSession(false);
    }
  };

  // Parse duration and type from a module name formatted as:
  // <name>-<account>-<ignore>-<type>-<duration_quantity><duration_time>
  // Spaces are stripped before parsing; type/duration are taken from the
  // last two hyphen-delimited segments to handle names containing hyphens.
  const parseModuleName = (name) => {
    const cleaned = (name || "").replace(/\s+/g, "");
    const parts = cleaned.split("-");
    if (parts.length < 2) return {};

    const typeCode = parts[parts.length - 2].toLowerCase();
    const durationStr = parts[parts.length - 1].toLowerCase();

    let type = null;
    if (typeCode === "inp") type = "In-Person";
    else if (typeCode === "v") type = "Virtual";

    let duration_quantity = null;
    let duration_time = null;
    const durationMatch = durationStr.match(/^(\d+(?:\.\d+)?)(hrs?|days?|mins?)$/i);
    if (durationMatch) {
      const rawQuantity = parseFloat(durationMatch[1]);
      const unit = durationMatch[2].toLowerCase();
      if (unit === "hr" || unit === "hrs") {
        duration_quantity = rawQuantity;
        duration_time = "Hour";
      } else if (unit === "day" || unit === "days") {
        duration_quantity = rawQuantity;
        duration_time = "Day";
      } else if (unit === "min" || unit === "mins") {
        // Sessions only support hour/day units, so convert minutes to hours
        // (e.g. 30mins -> 0.5, 90mins -> 1.5).
        duration_quantity = rawQuantity / 60;
        duration_time = "Hour";
      }
    }

    return { type, duration_quantity, duration_time };
  };

  const [creatingSessionsFromModules, setCreatingSessionsFromModules] =
    useState(false);

  // Open the Create Sessions confirmation modal (allows picking the training location)
  const openCreateSessionsModal = () => {
    setCreateSessionsLocationId(
      job?.default_training_location_id
        ? String(job.default_training_location_id)
        : "",
    );
    setShowCreateSessionsModal(true);
  };

  // Create one session per top-level module in the programme. Type comes from the
  // programme's Delivery and duration from the module's Duration (minutes, converted
  // to hours); anything unset falls back to parsing the module's name
  // (format: name-account-ignore-type-qty+unit).
  const handleCreateSessionsFromModules = async () => {
    const programme = job.programme_data || {};
    const modules = (job.programme_content || []).filter(
      (item) => item.type === "module",
    );
    if (modules.length === 0) {
      alert("No modules found in the selected programme.");
      return;
    }
    const programmeType =
      programme.delivery || parseModuleName(programme.name).type || null;

    setShowCreateSessionsModal(false);
    setCreatingSessionsFromModules(true);
    try {
      const locId = createSessionsLocationId
        ? parseInt(createSessionsLocationId, 10)
        : null;
      for (const mod of modules) {
        const parsed = parseModuleName(mod.name);
        const type = programme.delivery || parsed.type || programmeType;
        const moduleMinutes = parseFloat(mod.duration);
        const [duration_quantity, duration_time] =
          moduleMinutes > 0
            ? [moduleMinutes / 60, "Hour"]
            : [parsed.duration_quantity, parsed.duration_time];
        await axios.post(
          "/sessions/",
          {
            job_id: job.id,
            title: null,
            type: type || null,
            duration_quantity: duration_quantity || null,
            duration_time: duration_time || null,
            location_id: locId,
            component_ids: [mod.component_id],
          },
          { withCredentials: true },
        );
      }
      const jobResponse = await axios.get(`/jobs/${id}/`, {
        withCredentials: true,
      });
      setJob(jobResponse.data);
      setSessions(jobResponse.data.sessions || []);
    } catch (err) {
      console.error("Failed to create sessions from modules:", err);
      alert("Failed to create sessions from modules");
    } finally {
      setCreatingSessionsFromModules(false);
    }
  };

  // Save changes
  // Delete job
  const handleDelete = async () => {
    try {
      await axios.delete(`/jobs/${job.id}/`, { withCredentials: true });
      window.location.href = "/jobs";
    } catch (err) {
      console.error("Failed to delete job:", err);
    }
  };

  // Load programmes for dropdown
  const loadProgrammes = async () => {
    if (programmes || programmesLoading) return;
    setProgrammesLoading(true);
    try {
      const response = await axios.get("/programmes");
      const body = response.data;
      const list = body?.data ?? [];
      // Filter to only show non-archived custom and template programmes (not files)
      const filteredList = list.filter(
        (prog) =>
          (prog.type === "custom" || prog.type === "template") &&
          !prog.archived_at,
      );
      setProgrammes(filteredList);
    } catch (err) {
      console.error("Failed to load programmes:", err);
    } finally {
      setProgrammesLoading(false);
    }
  };

  // Eagerly load programme options so the inline-edit dropdown has them ready
  useEffect(() => {
    loadProgrammes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle objectives input change
  const handleObjectiveChange = (e) => {
    const { name, value } = e.target;
    setObjectives((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const autoResizeTextarea = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    if (!showObjectivesModal) return;
    // Resize after the modal content is mounted
    const t = window.setTimeout(() => {
      const textareas = document.querySelectorAll(".job-objective-textarea");
      textareas.forEach((ta) => autoResizeTextarea(ta));
    }, 0);
    // Fetch objective options from global variables
    axios
      .get("/global-variables/")
      .then((res) => {
        const vars = res.data.data || [];
        const objectivesVar = vars.find((v) => v.key === "proposal_objectives");
        const overviewVar = vars.find((v) => v.key === "proposal_overview");
        setObjectiveOptions(objectivesVar?.value || []);
        setOverviewOptions(overviewVar?.value || []);
      })
      .catch((err) => console.error("Failed to load objective options:", err));
    return () => window.clearTimeout(t);
  }, [showObjectivesModal]);

  // Re-resize textareas whenever objectives change (e.g. after selecting from dropdown)
  useEffect(() => {
    if (!showObjectivesModal) return;
    const t = window.setTimeout(() => {
      document
        .querySelectorAll(".job-objective-textarea")
        .forEach(autoResizeTextarea);
    }, 0);
    return () => window.clearTimeout(t);
  }, [objectives, showObjectivesModal]);

  // Track html.dark class changes so the PDF iframe color-scheme stays in sync
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  const previewPollRef = useRef(null);

  // Stop the poll interval (used on modal close).
  const clearPreviewPoll = () => {
    if (previewPollRef.current) {
      window.clearInterval(previewPollRef.current);
      previewPollRef.current = null;
    }
  };

  // Start polling if not already running. The server guarantees that ready:true
  // means the LATEST queued task has completed — older tasks self-abort so they
  // never clear the pending marker for a newer request.
  const startPreviewPollIfNeeded = (jobId) => {
    if (previewPollRef.current) return;

    previewPollRef.current = window.setInterval(() => {
      axios
        .get(`/jobs/${jobId}/proposal-preview-status/`, {
          withCredentials: true,
        })
        .then((statusRes) => {
          if (statusRes.data.ready) {
            window.clearInterval(previewPollRef.current);
            previewPollRef.current = null;
            setProposalPreviewUrl(
              `/api/jobs/${jobId}/proposal-preview/?t=${Date.now()}`,
            );
            setProposalPreviewLoading(false);
          }
          // else: latest task still running — keep polling
        })
        .catch(() => {
          window.clearInterval(previewPollRef.current);
          previewPollRef.current = null;
          setProposalPreviewLoading(false);
        });
    }, 1000);
  };

  // On modal open, immediately show any already-generated preview (survives page navigation)
  useEffect(() => {
    if (!showObjectivesModal || !job || proposalPreviewUrl) return;
    axios
      .head(`/jobs/${job.id}/proposal-preview/`, { withCredentials: true })
      .then(() =>
        setProposalPreviewUrl(`/api/jobs/${job.id}/proposal-preview/`),
      )
      .catch(() => {}); // no existing preview — the refresh effect will generate one
  }, [showObjectivesModal, job]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up poll when the modal closes.
  useEffect(() => {
    if (!showObjectivesModal) {
      clearPreviewPoll();
      setProposalPreviewLoading(false);
    }
  }, [showObjectivesModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fire a new preview request whenever the modal is open and form data changes.
  // The server cancels any previous queued tasks for this job, so the poll only
  // ever waits for the most-recent request.
  useEffect(() => {
    if (!showObjectivesModal || !job) return;

    setProposalPreviewLoading(true);

    const debounce = window.setTimeout(() => {
      axios
        .post(
          "/jobs/create-proposal-preview/",
          {
            job_id: job.id,
            objectives,
            overview: selectedOverview,
          },
          { withCredentials: true },
        )
        .then((res) => {
          if (res.data.queued) {
            startPreviewPollIfNeeded(job.id);
          } else {
            setProposalPreviewLoading(false);
          }
        })
        .catch(() => setProposalPreviewLoading(false));
    }, 800);

    return () => {
      window.clearTimeout(debounce);
    };
  }, [showObjectivesModal, objectives, selectedOverview]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helpers for the objectives list UI
  const getObjectivesArray = () =>
    [1, 2, 3, 4, 5].map((n) => objectives[`objective${n}`]).filter((v) => v);

  const setObjectivesFromArray = (arr) => {
    const next = {
      objective1: "",
      objective2: "",
      objective3: "",
      objective4: "",
      objective5: "",
    };
    arr.forEach((v, i) => {
      next[`objective${i + 1}`] = v;
    });
    setObjectives(next);
  };

  const handleObjectiveTextChange = (index, value) => {
    const arr = getObjectivesArray();
    if (value === "") {
      arr.splice(index, 1);
    } else {
      arr[index] = value;
    }
    setObjectivesFromArray(arr);
  };

  const handleObjectiveSelect = (text) => {
    const arr = getObjectivesArray();
    if (arr.length >= 5) return;
    arr.push(text);
    setObjectivesFromArray(arr);
  };

  // Save objectives only
  const handleSaveObjectives = async () => {
    try {
      await axios.patch(
        `/proposals/${job.id}/`,
        { ...objectives, overview: selectedOverview },
        { withCredentials: true },
      );
      setShowObjectivesModal(false);
    } catch (error) {
      console.error("Error saving objectives:", error);
      alert("Failed to save objectives");
    }
  };

  // Save objectives and create proposal
  const handleSaveAndCreateProposal = async () => {
    setCreatingProposal(true);
    try {
      // First save objectives
      await axios.patch(
        `/proposals/${job.id}/`,
        { ...objectives, overview: selectedOverview },
        { withCredentials: true },
      );

      // Then create proposal
      const response = await axios.post(
        "/jobs/create-proposal/",
        { job_id: job.id },
        { withCredentials: true },
      );

      if (response.data.success) {
        console.log(
          `Proposal generation started. Task ID: ${response.data.task_id}`,
        );
        setShowObjectivesModal(false);
        alert("Proposal creation process started - check SharePoint");
        refreshTasks();
      }
    } catch (err) {
      console.error("Failed to create proposal:", err);
      const errorMessage =
        err.response?.data?.error || "Failed to create proposal";
      alert(`Error: ${errorMessage}`);
    } finally {
      setCreatingProposal(false);
    }
  };

  // Open objectives modal
  const handleCreateProposal = async () => {
    try {
      const response = await axios.get(`/jobs/${id}/`, {
        withCredentials: true,
      });
      const data = response.data;
      setJob(data);
      setSessions(data.sessions || []);
      if (data.proposal) {
        setObjectives({
          objective1: data.proposal.objective1 || "",
          objective2: data.proposal.objective2 || "",
          objective3: data.proposal.objective3 || "",
          objective4: data.proposal.objective4 || "",
          objective5: data.proposal.objective5 || "",
        });
        if (data.proposal.overview) {
          setSelectedOverview(data.proposal.overview);
        }
      }
    } catch (err) {
      console.error(
        "Failed to refresh job before opening proposal modal:",
        err,
      );
    }
    setShowObjectivesModal(true);
  };

  // Create booking form
  const handleCreateBookingForm = async () => {
    setCreatingBookingForm(true);
    try {
      const response = await axios.post(
        "/jobs/create-booking-form/",
        { job_id: job.id },
        { withCredentials: true },
      );
      if (response.data.success) {
        console.log(
          `Booking form generation started. Task ID: ${response.data.task_id}`,
        );
        refreshTasks();
      }
    } catch (err) {
      console.error("Failed to create booking form:", err);
      const errorMessage =
        err.response?.data?.error || "Failed to create booking form";
      alert(`Error: ${errorMessage}`);
    } finally {
      setCreatingBookingForm(false);
    }
  };

  // Create slideshow
  const handleCreateSlideshow = async () => {
    setCreatingSlideshow(true);
    try {
      const response = await axios.post(
        "/jobs/create-slideshow/",
        { job_id: job.id },
        { withCredentials: true },
      );

      if (response.data.success) {
        // Slideshow generation started successfully - no alert needed
        console.log(
          `Slideshow generation started. Task ID: ${response.data.task_id}`,
        );
      }
    } catch (err) {
      console.error("Failed to create slideshow:", err);
      const errorMessage =
        err.response?.data?.error || "Failed to create slideshow";
      alert(`Error: ${errorMessage}`);
    } finally {
      setCreatingSlideshow(false);
    }
  };

  // Callback functions for cross-table refreshing
  const handleTaskCreated = () => {
    // When an interaction becomes a task, refresh tasks table
    if (tasksTableRef.current && tasksTableRef.current.fetchTasks) {
      tasksTableRef.current.fetchTasks();
    }
  };

  const handleInteractionCreated = () => {
    // When a task becomes an interaction, refresh interactions table
    if (
      interactionsTableRef.current &&
      interactionsTableRef.current.fetchInteractions
    ) {
      interactionsTableRef.current.fetchInteractions();
    }
  };

  if (loading)
    return (
      <div>
        <p>Loading job...</p>
      </div>
    );
  if (!job)
    return (
      <div>
        <p>Job not found.</p>
      </div>
    );

  // Fallback display name when the job has no title: "<account> - <programme>",
  // using the programme's public name (falling back to its internal name).
  const jobFallbackName = () => {
    const accountName =
      job.account_data?.name_short || job.account_data?.name_long || "";
    const programmeName =
      job.programme_data?.public_name || job.programme_data?.name || "";
    const parts = [accountName, programmeName].filter(Boolean);
    return parts.length ? parts.join(" - ") : `Job #${job.id}`;
  };
  const jobDisplayName = job.title || jobFallbackName();

  const defaultParticipants = user
    ? [
        {
          id: user.user_contact_id,
          type: "contact",
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          is_sender: false,
        },
      ]
    : [];

  // Format date display
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const currencies = ["GBP", "USD"];

  const datesReason = "Set by the job's sessions (first and last session dates)";

  // Flat options list for the training location dropdown:
  // client account's locations first, then other accounts' venue locations.
  const clientAccountId = job.account_data?.id;
  const trainingLocationOptions = [
    ...allTrainingLocations.filter((l) => l.account_id === clientAccountId),
    ...allTrainingLocations.filter(
      (l) => l.venue && l.account_id !== clientAccountId,
    ),
  ].map((loc) => ({
    value: String(loc.id),
    label:
      loc.account_id === clientAccountId
        ? locationLabel(loc)
        : `${locationLabel(loc)} (${loc.account_name || ""})`,
  }));

  const sortedSessions = [...sessions].sort((a, b) => {
    if (!a.start_time && !b.start_time) return 0;
    if (!a.start_time) return 1;
    if (!b.start_time) return -1;
    return a.start_time < b.start_time ? -1 : a.start_time > b.start_time ? 1 : 0;
  });
  const sessionNumberMap = new Map(sortedSessions.map((s, i) => [s.id, i + 1]));

  return (
    <div className="page-layout">
      <PageTitle>
        {typeIcons["job"]} {jobDisplayName}
      </PageTitle>
      <div className="page-header">
        <div className="page-header-buttons">
          <button onClick={handleCreateProposal} disabled={creatingProposal}>
            {creatingProposal ? "Creating Proposal..." : "Create Proposal"}
          </button>
          <button
            onClick={handleCreateBookingForm}
            disabled={creatingBookingForm}
          >
            {creatingBookingForm
              ? "Creating Booking Form..."
              : "Create Booking Form"}
          </button>
          <button onClick={handleCreateSlideshow} disabled={creatingSlideshow}>
            {creatingSlideshow
              ? "Creating Slideshow(s)..."
              : "Create Slideshow(s)"}
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="delete-btn"
          >
            Delete Job
          </button>
        </div>
      </div>

      <div className="page-content">
        <table className="details-table">
          <tbody>
            <tr>
              <td>
                <div className="details-label">Title</div>
                <InlineEdit
                  field="title"
                  value={job.title || ""}
                  onSave={handleInlineSave}
                />
              </td>
              <td>
                <div className="details-label">Stage</div>
                <InlineEdit
                  field="stage"
                  type="dropdown"
                  value={job.stage || ""}
                  options={fieldOptionList(fieldOptions, "jobs", "stage")}
                  onSave={handleInlineSave}
                />
              </td>
            </tr>
            <tr>
              <td>
                <div className="details-label">Start Date</div>
                <InlineEdit
                  field="start_date"
                  type="date"
                  value={job.start_date || ""}
                  displayValue={formatJobDate(job.start_date)}
                  disabled={job.dates_from_sessions}
                  disabled_reason={datesReason}
                  validate={(v) =>
                    v && job.end_date && v > job.end_date
                      ? "Start date can't be after the end date"
                      : null
                  }
                  onSave={handleInlineSave}
                />
              </td>
              <td>
                <div className="details-label">End Date</div>
                <InlineEdit
                  field="end_date"
                  type="date"
                  value={job.end_date || ""}
                  displayValue={formatJobDate(job.end_date)}
                  disabled={job.dates_from_sessions}
                  disabled_reason={datesReason}
                  validate={(v) =>
                    v && job.start_date && v < job.start_date
                      ? "End date can't be before the start date"
                      : null
                  }
                  onSave={handleInlineSave}
                />
              </td>
            </tr>
            <tr>
              <td>
                <div className="details-label">Currency</div>
                <InlineEdit
                  field="currency"
                  type="dropdown"
                  value={job.currency || ""}
                  options={currencies.map((curr) => ({
                    value: curr,
                    label: curr,
                  }))}
                  onSave={handleInlineSave}
                />
              </td>
              <td>
                <div className="details-label">Team</div>
                <InlineEdit
                  field="team"
                  value={job.team || ""}
                  onSave={handleInlineSave}
                />
              </td>
            </tr>
            <tr>
              <td>
                <div className="details-label">Client Account</div>
                <InlineEdit
                  field="account"
                  type="account"
                  value={job.account_data}
                  displayValue={
                    job.account_data?.id ? (
                      <Link to={`/account?id=${job.account_data.id}`}>
                        {job.account_data.name_short ||
                          job.account_data.name_long}
                      </Link>
                    ) : (
                      ""
                    )
                  }
                  onSave={handleInlineSave}
                />
              </td>
              <td></td>
            </tr>
            <tr>
              <td>
                <div className="details-label">Account Contact 1</div>
                <InlineEdit
                  field="account_contact_1"
                  type="contact"
                  value={job.account_contact_1_data}
                  contactAccountId={job.account_data?.id}
                  displayValue={
                    job.account_contact_1_data?.id ? (
                      <Link to={`/contact?id=${job.account_contact_1_data.id}`}>
                        {job.account_contact_1_data.first_name}{" "}
                        {job.account_contact_1_data.last_name}
                      </Link>
                    ) : (
                      ""
                    )
                  }
                  onSave={handleInlineSave}
                />
              </td>
              <td>
                {job.account_contact_id_1 && (
                  <>
                    <div className="details-label">Account Contact 2</div>
                    <InlineEdit
                      field="account_contact_2"
                      type="contact"
                      value={job.account_contact_2_data}
                      contactAccountId={job.account_data?.id}
                      displayValue={
                        job.account_contact_2_data?.id ? (
                          <Link
                            to={`/contact?id=${job.account_contact_2_data.id}`}
                          >
                            {job.account_contact_2_data.first_name}{" "}
                            {job.account_contact_2_data.last_name}
                          </Link>
                        ) : (
                          ""
                        )
                      }
                      onSave={handleInlineSave}
                    />
                  </>
                )}
              </td>
            </tr>
            <tr>
              <td>
                <div className="details-label">FTL Contact</div>
                <InlineEdit
                  field="ftl_contact"
                  type="contact"
                  value={job.ftl_contact_data}
                  displayValue={
                    job.ftl_contact_data?.id ? (
                      <Link to={`/contact?id=${job.ftl_contact_data.id}`}>
                        {job.ftl_contact_data.first_name}{" "}
                        {job.ftl_contact_data.last_name}
                      </Link>
                    ) : (
                      ""
                    )
                  }
                  onSave={handleInlineSave}
                />
              </td>
              <td>
                <div className="details-label">Equipment</div>
                <InlineEdit
                  field="equipment"
                  value={job.equipment || ""}
                  onSave={handleInlineSave}
                />
              </td>
            </tr>
            <tr>
              <td>
                <div className="details-label">Programme</div>
                <InlineEdit
                  field="programme"
                  type="dropdown"
                  value={job.programme_id ? String(job.programme_id) : ""}
                  displayValue={job.programme_data?.name || ""}
                  options={(programmes || []).map((p) => ({
                    value: String(p.id),
                    label: p.name,
                  }))}
                  onSave={handleInlineSave}
                />
              </td>
              <td>
                <div className="details-label">Default Training Location</div>
                <InlineEdit
                  field="default_training_location"
                  type="dropdown"
                  value={
                    job.default_training_location_id
                      ? String(job.default_training_location_id)
                      : ""
                  }
                  displayValue={locationWithAddress(
                    job.default_training_location_data,
                  )}
                  options={trainingLocationOptions}
                  onSave={handleInlineSave}
                />
              </td>
            </tr>
            <tr>
              <td>
                <div className="details-label">Virtual Setup</div>
                <InlineEdit
                  field="virtual_setup"
                  type="dropdown"
                  value={
                    job.virtual_type && job.virtual_platform
                      ? `${job.virtual_type}|${job.virtual_platform}`
                      : ""
                  }
                  displayValue={virtualSetupLabel(job)}
                  options={(fieldOptions.jobs?.virtual_type || []).flatMap((type) =>
                    (fieldOptions.jobs?.virtual_platform || []).map((platform) => ({
                      value: `${type}|${platform}`,
                      label: virtualSetupLabel({
                        virtual_type: type,
                        virtual_platform: platform,
                      }),
                    })),
                  )}
                  onSave={handleInlineSave}
                />
              </td>
              <td>
                <div className="details-label">Default Virtual Link</div>
                <InlineEdit
                  field="default_virtual_link"
                  value={job.default_virtual_link || ""}
                  onSave={handleInlineSave}
                />
              </td>
            </tr>
            <tr>
              <td>
                <div className="details-label">Category</div>
                <InlineEdit
                  field="category"
                  type="dropdown"
                  value={job.category || ""}
                  options={fieldOptionList(fieldOptions, "jobs", "category")}
                  onSave={handleInlineSave}
                />
              </td>
              <td>
                <div className="details-label">Job Type</div>
                <InlineEdit
                  field="job_type"
                  value={job.job_type || ""}
                  disabled
                  disabled_reason="Set by the job's sessions: their type if they all share one, otherwise Mixed"
                />
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Billing</h3>
        <table className="details-table">
          <tbody>
            <tr>
              <td>
                <div className="details-label">Billing Account</div>
                <InlineEdit
                  field="invoice_account"
                  type="account"
                  value={job.invoice_account_data}
                  displayValue={
                    job.invoice_account_data?.id ? (
                      <Link to={`/account?id=${job.invoice_account_data.id}`}>
                        {job.invoice_account_data.name_short ||
                          job.invoice_account_data.name_long}
                      </Link>
                    ) : (
                      ""
                    )
                  }
                  onSave={handleInlineSave}
                />
              </td>
              <td>
                <div className="details-label">Billing Contact</div>
                <InlineEdit
                  field="invoice_contact"
                  type="contact"
                  value={job.invoice_contact_data}
                  contactAccountId={job.invoice_account_data?.id}
                  displayValue={
                    job.invoice_contact_data?.id ? (
                      <ContactWithEmail contact={job.invoice_contact_data} />
                    ) : (
                      ""
                    )
                  }
                  onSave={handleInlineSave}
                />
              </td>
            </tr>
            <tr>
              <td>
                <div className="details-label">Billing Address</div>
                <InlineEdit
                  field="invoice_location"
                  type="dropdown"
                  value={
                    job.invoice_location_id
                      ? String(job.invoice_location_id)
                      : ""
                  }
                  displayValue={locationWithAddress(job.invoice_location_data)}
                  options={invoiceLocations.map((loc) => ({
                    value: String(loc.id),
                    label: locationLabel(loc),
                  }))}
                  disabled={!job.invoice_account_data?.id}
                  disabled_reason="Select an invoice account first"
                  onSave={handleInlineSave}
                />
              </td>
              <td>
                <div className="details-label">PO Number</div>
                <InlineEdit
                  field="invoice_po_number"
                  value={job.invoice_po_number || ""}
                  onSave={handleInlineSave}
                />
              </td>
            </tr>
            <tr>
              <td>
                <div className="details-label">Sales Channel</div>
                <InlineEdit
                  field="sales_channel"
                  type="dropdown"
                  value={job.sales_channel || ""}
                  options={fieldOptionList(fieldOptions, "jobs", "sales_channel")}
                  onSave={handleInlineSave}
                />
              </td>
              <td>
                <div className="details-label">Invoicing Platform</div>
                <InlineEdit
                  field="invoicing_platform_account"
                  type="account"
                  value={job.invoicing_platform_account_data}
                  displayValue={
                    job.invoicing_platform_account_data?.id ? (
                      <Link
                        to={`/account?id=${job.invoicing_platform_account_data.id}`}
                      >
                        {job.invoicing_platform_account_data.name_short ||
                          job.invoicing_platform_account_data.name_long}
                      </Link>
                    ) : (
                      ""
                    )
                  }
                  onSave={handleInlineSave}
                />
              </td>
            </tr>
          </tbody>
        </table>

        <h3>Notes</h3>
        <NotesTable jobId={job.id} jobName={jobDisplayName} />

        <hr />

        <h3>Sessions</h3>
        <div style={{ display: "flex", alignSelf: "flex-start" }}>
          <button
            onClick={openCreateSessionsModal}
            disabled={
              creatingSessionsFromModules ||
              !job.programme_content?.some((item) => item.type === "module")
            }
          >
            {creatingSessionsFromModules
              ? "Creating Sessions..."
              : "Create Sessions From Programme"}
          </button>
          <button onClick={openAddSessionModal}>+ Add Ad-hoc Session</button>
        </div>
        <Table
          data={sortedSessions}
          columns={[
            {
              key: "title",
              header: "#",
              type: "static",
              sortable: false,
              width: 32,
              render: (value, row) => (
                <Link to={`/session?id=${row.id}`}>
                  {sessionNumberMap.get(row.id) ?? '?'}
                </Link>
              ),
            },
            {
              key: "module_name",
              header: "Module",
              type: "static",
              sortable: false,
              width: 128,
              render: (value, row) => row.module_name || "—",
            },
            {
              key: "date",
              header: "Date",
              type: "static",
              sortable: false,
              width: 80,
              render: (value, row) => {
                if (!row.start_time) return "—";
                try {
                  return new Date(row.start_time).toLocaleDateString("en-GB");
                } catch {
                  return row.start_time.slice(0, 10);
                }
              },
            },
            {
              key: "time",
              header: "Time",
              type: "static",
              sortable: false,
              width: 80,
              render: (value, row) => {
                const start = row.start_time ? row.start_time.slice(11, 16) : null;
                const end = row.end_time ? row.end_time.slice(11, 16) : null;
                if (!start && !end) return "—";
                if (start && end) return `${start}–${end}`;
                return start || end;
              },
            },
            {
              key: "duration",
              header: "Duration",
              type: "static",
              sortable: false,
              width: 80,
              render: (value, row) => {
                if (row.duration_quantity && row.duration_time) {
                  return `${row.duration_quantity} ${row.duration_time.toLowerCase()}`;
                }
                return "—";
              },
            },
            {
              key: "tutor",
              header: "Tutor",
              type: "static",
              sortable: false,
              width: 112,
              render: (value, row) => {
                if (row.tutor_data) {
                  return (
                    <Link to={`/contact?id=${row.tutor_data.id}`}>
                      {row.tutor_data.first_name} {row.tutor_data.last_name}
                    </Link>
                  );
                }
                return "—";
              },
            },
            {
              key: "location",
              header: "Location",
              type: "static",
              sortable: false,
              width: 112,
              render: (value, row) => locationLabel(row.location_data) || "—",
            },
            {
              key: "type",
              header: "Type",
              type: "static",
              sortable: false,
              width: 80,
              render: (value) => value || "—",
            },
            {
              key: "actions",
              header: "Actions",
              type: "static",
              sortable: false,
              width: 96,
              render: (value, row) => (
                <div>
                  <button onClick={() => openEditSessionModal(row)}>
                    Edit
                  </button>
                  <button
                    onClick={() => openDeleteSessionConfirm(row)}
                    className="delete-btn"
                  >
                    Delete
                  </button>
                </div>
              ),
            },
          ]}
          loading={false}
          emptyMessage="No sessions found for this job."
        />

        <hr />

        <h3>Tasks</h3>
        <TasksTable
          jobId={job.id}
          defaultParticipants={defaultParticipants}
          onInteractionCreated={handleInteractionCreated}
          ref={tasksTableRef}
        />

        <h3>Interactions</h3>
        <InteractionsTable
          jobId={job.id}
          defaultParticipants={defaultParticipants}
          onTaskCreated={handleTaskCreated}
          ref={interactionsTableRef}
        />

        {/* Delete Modal */}
        <Modal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          title="Confirm Delete Job"
        >
          <p>
            Are you sure you want to delete{" "}
            <strong>{jobDisplayName}</strong>? This action cannot
            be undone.
          </p>
          <div>
            <button onClick={handleDelete} style={{ color: "red" }}>
              Yes, Delete
            </button>
            <button onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
          </div>
        </Modal>

        {/* Create Session Modal */}
        <Modal
          isOpen={showCreateSession}
          onClose={closeSessionModal}
          title={editingSessionId ? "Edit Session" : "Create New Session"}
        >
          <div className="form">
            <label>
              Title (optional):
              <input
                autoComplete="off"
                type="text"
                name="title"
                value={sessionFormData.title}
                onChange={handleSessionFormChange}
                placeholder="Session title..."
              />
            </label>

            <label>
              Date:
              <input
                autoComplete="off"
                type="date"
                name="session_date"
                value={sessionFormData.session_date}
                onChange={handleSessionFormChange}
              />
            </label>

            <label>
              Start Time:
              <input
                autoComplete="off"
                type="time"
                name="start_time"
                value={sessionFormData.start_time}
                onChange={handleSessionFormChange}
              />
            </label>

            <label>
              End Time:
              <input
                autoComplete="off"
                type="time"
                name="end_time"
                value={sessionFormData.end_time}
                onChange={handleSessionFormChange}
              />
            </label>

            <label>
              Duration:
              <div>
                <select
                  autoComplete="off"
                  name="duration_quantity"
                  value={sessionFormData.duration_quantity}
                  onChange={handleSessionFormChange}
                  style={{ flex: 1 }}
                  disabled={!sessionFormData.duration_time}
                >
                  <option value="">—</option>
                  {sessionFormData.duration_time === "Hour" && (
                    <>
                      <option value="1">1</option>
                      <option value="1.5">1.5</option>
                      <option value="2">2</option>
                      <option value="2.5">2.5</option>
                      <option value="3">3</option>
                      <option value="3.5">3.5</option>
                      <option value="4">4</option>
                      <option value="8">8</option>
                    </>
                  )}
                  {sessionFormData.duration_time === "Day" && (
                    <>
                      <option value="0.5">0.5</option>
                      <option value="1">1</option>
                    </>
                  )}
                </select>
                <select
                  autoComplete="off"
                  name="duration_time"
                  value={sessionFormData.duration_time}
                  onChange={handleSessionFormChange}
                  style={{ flex: 1 }}
                >
                  <option value="">Select unit...</option>
                  <option value="Hour">Hour(s)</option>
                  <option value="Day">Day(s)</option>
                </select>
              </div>
            </label>

            <label>
              Type:
              <select
                autoComplete="off"
                name="type"
                value={sessionFormData.type}
                onChange={handleSessionFormChange}
              >
                <option value="">Select type...</option>
                <option value="In-Person">In-Person</option>
                <option value="Virtual">Virtual</option>
              </select>
            </label>

            {sessionFormData.type === "In-Person" && (
              <label>
                Training Location:
                <select
                  autoComplete="off"
                  name="location_id"
                  value={sessionFormData.location_id}
                  onChange={handleSessionFormChange}
                >
                  <option value="">No location</option>
                  {clientAccountId &&
                    allTrainingLocations.filter(
                      (l) => l.account_id === clientAccountId,
                    ).length > 0 && (
                      <optgroup label="Client Account">
                        {allTrainingLocations
                          .filter((l) => l.account_id === clientAccountId)
                          .map((loc) => (
                            <option key={loc.id} value={String(loc.id)}>
                              {locationLabel(loc)}
                            </option>
                          ))}
                      </optgroup>
                    )}
                  {allTrainingLocations.filter(
                    (l) => l.venue && l.account_id !== clientAccountId,
                  ).length > 0 && (
                    <optgroup label="Venues">
                      {allTrainingLocations
                        .filter(
                          (l) => l.venue && l.account_id !== clientAccountId,
                        )
                        .map((loc) => (
                          <option key={loc.id} value={String(loc.id)}>
                            {locationLabel(loc)} (
                            {loc.account_name || ""})
                          </option>
                        ))}
                    </optgroup>
                  )}
                </select>
              </label>
            )}
            {sessionFormData.type === "Virtual" && (
              <label>
                {virtualSetupLabel(job)
                  ? `Meeting Link (${virtualSetupLabel(job)}):`
                  : "Meeting Link:"}
                <input
                  autoComplete="off"
                  type="text"
                  name="virtual_link"
                  value={sessionFormData.virtual_link}
                  onChange={handleSessionFormChange}
                  placeholder="https://..."
                />
              </label>
            )}

            <div>
              <label>Programme Component:</label>
              <div className="component-list">
                {job?.programme_content && job.programme_content.length > 0 ? (
                  <>
                    <label className="component-item component-label" style={{ cursor: "pointer" }}>
                      <input
                        autoComplete="off"
                        type="radio"
                        name="session-component"
                        checked={selectedComponents.length === 0}
                        onChange={() => setSelectedComponents([])}
                      />
                      <span className="component-text">None</span>
                    </label>
                    {job.programme_content.map((component) => (
                      <label
                        key={component.component_id}
                        className="component-item component-label"
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          autoComplete="off"
                          type="radio"
                          name="session-component"
                          checked={selectedComponents.includes(component.component_id)}
                          onChange={() => setSelectedComponents([component.component_id])}
                        />
                        <span className="component-text">
                          {component.type === "module" && "Module: "}
                          {component.type === "block" && "Block: "}
                          {component.type === "element" && "Slide: "}
                          {component.name ||
                            component.slide_name ||
                            `${component.type} #${component.component_id}`}
                          {(component.type === "module" ||
                            component.type === "block") &&
                            component.linked && (
                              <IconLink
                                aria-label="Linked"
                                title={linkedComponentTooltip}
                                style={{ marginLeft: 6, verticalAlign: "middle" }}
                              />
                            )}
                          {component.type === "block" &&
                            component.duration &&
                            ` (${component.duration} min)`}
                        </span>
                      </label>
                    ))}
                  </>
                ) : (
                  <div className="component-empty">
                    {job?.programme_data
                      ? "No components in this programme"
                      : "No programme assigned to this job"}
                  </div>
                )}
              </div>
            </div>

            <div>
              <button onClick={handleSaveSession} disabled={creatingSession}>
                {creatingSession
                  ? editingSessionId
                    ? "Saving..."
                    : "Creating..."
                  : editingSessionId
                    ? "Save Changes"
                    : "Create Session"}
              </button>
              <button onClick={closeSessionModal}>Cancel</button>
            </div>
          </div>
        </Modal>

        {/* Delete Session Confirmation Modal */}
        <Modal
          isOpen={sessionDeleteConfirm}
          onClose={() => setSessionDeleteConfirm(false)}
          title="Confirm Delete Session"
        >
          <p>
            Are you sure you want to delete{" "}
            <strong>
              {sessionToDelete?.title || `Session #${sessionToDelete?.id}`}
            </strong>
            ? This action cannot be undone.
          </p>
          <div>
            <button
              onClick={handleDeleteSession}
              disabled={deletingSession}
              style={{ color: "red" }}
            >
              {deletingSession ? "Deleting..." : "Yes, Delete"}
            </button>
            <button onClick={() => setSessionDeleteConfirm(false)}>
              Cancel
            </button>
          </div>
        </Modal>

        {/* Create Sessions from Modules Modal */}
        <Modal
          isOpen={showCreateSessionsModal}
          onClose={() => setShowCreateSessionsModal(false)}
          title="Create Sessions from Modules"
        >
          <div className="form">
            <p>
              Creates one session per module in the selected programme, using
              the programme's Delivery and each module's Duration. Anything not
              set there is parsed from the module names.
            </p>
            <label>
              Training Location:
              <select
                autoComplete="off"
                value={createSessionsLocationId}
                onChange={(e) => setCreateSessionsLocationId(e.target.value)}
              >
                <option value="">No location</option>
                {clientAccountId &&
                  allTrainingLocations.filter(
                    (l) => l.account_id === clientAccountId,
                  ).length > 0 && (
                    <optgroup label="Client Account">
                      {allTrainingLocations
                        .filter((l) => l.account_id === clientAccountId)
                        .map((loc) => (
                          <option key={loc.id} value={String(loc.id)}>
                            {locationLabel(loc)}
                          </option>
                        ))}
                    </optgroup>
                  )}
                {allTrainingLocations.filter(
                  (l) => l.venue && l.account_id !== clientAccountId,
                ).length > 0 && (
                  <optgroup label="Venues">
                    {allTrainingLocations
                      .filter(
                        (l) => l.venue && l.account_id !== clientAccountId,
                      )
                      .map((loc) => (
                        <option key={loc.id} value={String(loc.id)}>
                          {locationLabel(loc)} (
                          {loc.account_name || ""})
                        </option>
                      ))}
                  </optgroup>
                )}
              </select>
            </label>
            <div>
              <button
                onClick={handleCreateSessionsFromModules}
                disabled={creatingSessionsFromModules}
              >
                {creatingSessionsFromModules
                  ? "Creating..."
                  : "Create Sessions"}
              </button>
              <button onClick={() => setShowCreateSessionsModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      </div>

      {/* Objectives Modal */}
      <Modal
        isOpen={showObjectivesModal}
        onClose={() => {
          setShowObjectivesModal(false);
          clearPreviewPoll();
        }}
        title="Create Proposal"
        boxClassName="modal-wide"
      >
        <div style={{ display: "flex", gap: "24px", alignItems: "flex-start" }}>
          <div className="form" style={{ flex: "0 0 420px", minWidth: 0 }}>
            <h4 style={{ margin: "0 0 8px" }}>Objectives</h4>
            <table style={{ marginTop: 0 }}>
              <tbody>
                {getObjectivesArray().map((text, index) => (
                  <tr key={index}>
                    <td
                      style={{
                        paddingRight: "8px",
                        verticalAlign: "top",
                        paddingTop: "6px",
                        fontSize: "1.1em",
                        lineHeight: 1,
                        borderBottom: "none",
                      }}
                    >
                      •
                    </td>
                    <td style={{ width: "100%", borderBottom: "none" }}>
                      <textarea
                        autoComplete="off"
                        value={text}
                        onChange={(e) =>
                          handleObjectiveTextChange(index, e.target.value)
                        }
                        onInput={(e) => autoResizeTextarea(e.target)}
                        rows={1}
                        className="job-objective-textarea"
                        style={{
                          resize: "none",
                          overflow: "hidden",
                          width: "100%",
                        }}
                      />
                    </td>
                  </tr>
                ))}
                {getObjectivesArray().length < 5 && (
                  <tr>
                    <td
                      style={{
                        paddingRight: "8px",
                        verticalAlign: "middle",
                        fontSize: "1.1em",
                        lineHeight: 1,
                        borderBottom: "none",
                      }}
                    >
                      •
                    </td>
                    <td style={{ borderBottom: "none" }}>
                      <select
                        autoComplete="off"
                        value=""
                        onChange={(e) => {
                          if (e.target.value)
                            handleObjectiveSelect(e.target.value);
                        }}
                      >
                        <option value="">Select objective</option>
                        {objectiveOptions.map((group, gi) => (
                          <optgroup key={gi} label={group.group}>
                            {(group.list || []).map((opt, i) => (
                              <option key={i} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <h4 style={{ margin: "16px 0 8px" }}>Programme Overview</h4>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              {overviewOptions.map((item, i) => (
                <label
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 8px",
                    border: "1px solid var(--color-border, #ccc)",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontWeight: "normal",
                  }}
                >
                  <Checkbox
                    checked={selectedOverview.includes(item.name)}
                    onChange={(e) => {
                      setSelectedOverview((prev) =>
                        e.target.checked
                          ? [...prev, item.name]
                          : prev.filter((n) => n !== item.name),
                      );
                    }}
                  />
                  <span style={{ flex: 1 }}>
                    {item.name}
                    <span
                      title={item.text}
                      style={{
                        cursor: "help",
                        color: "var(--color-muted, #888)",
                        fontSize: "0.75em",
                        border: "1px solid currentColor",
                        borderRadius: "50%",
                        width: "13px",
                        height: "13px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginLeft: "8px",
                        verticalAlign: "middle",
                      }}
                    >
                      ?
                    </span>
                  </span>
                </label>
              ))}
            </div>

            <div style={{ marginTop: "12px" }}>
              <button onClick={handleSaveObjectives}>Save</button>
              <button
                onClick={handleSaveAndCreateProposal}
                disabled={creatingProposal}
              >
                {creatingProposal ? "Creating..." : "Save & Create Proposal"}
              </button>
            </div>
          </div>

          {/* PDF Preview panel */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: "6px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <h4 style={{ margin: 0 }}>Preview</h4>
              {proposalPreviewLoading && (
                <span
                  style={{
                    fontSize: "0.8em",
                    color: "var(--color-muted, #888)",
                  }}
                >
                  Generating…
                </span>
              )}
            </div>
            {proposalPreviewUrl ? (
              <div
                className="proposal-preview-iframe-wrapper"
                style={{ position: "relative", width: "100%", height: "75vh" }}
              >
                <iframe
                  key={proposalPreviewUrl}
                  src={proposalPreviewUrl}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "1px solid var(--color-border, #ccc)",
                    borderRadius: "4px",
                    opacity: proposalPreviewLoading ? 0.6 : 1,
                    transition: "opacity 0.2s",
                    colorScheme: isDarkMode ? "dark" : "light",
                  }}
                  title="Proposal preview"
                />
                {proposalPreviewLoading && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <FaCircleNotch
                      className="taskpanel-task-status-spin"
                      style={{
                        fontSize: "2.5rem",
                        color: "var(--color-primary, #3F617D)",
                        opacity: 0.9,
                      }}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "75vh",
                  border: "1px solid var(--color-border, #ccc)",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--color-muted, #888)",
                  fontSize: "0.9em",
                }}
              >
                {proposalPreviewLoading
                  ? "Generating preview…"
                  : "Preview will appear here"}
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Job;
